import { useCallback, useEffect, useState } from 'react';
import { useContextManager } from '@/contexts';
import { ContextDataService, ContextData, formatContextDataForDisplay, isContextDataComplete } from '@/contexts/contextDataService';
import { ContextObject, ContextType } from '@/contexts';

/**
 * Custom hook for easy context data access and management
 * 
 * This hook provides a simplified interface for working with context data,
 * including automatic data fetching, loading states, and error handling.
 */

export interface UseContextDataReturn {
  // Current context data
  currentContextData: ContextData | null;
  isContextDataLoading: boolean;
  contextDataError: string | null;
  
  // Actions
  refreshContextData: () => Promise<void>;
  clearContextData: () => void;
  
  // Utility functions
  getFormattedContextData: () => any;
  isDataComplete: () => boolean;
  
  // Fetch data for any context
  fetchContextData: (context: ContextObject) => Promise<ContextData | null>;
  fetchContextDataById: (contextType: ContextType, contextId: string | number) => Promise<ContextData | null>;
}

/**
 * Hook for accessing current context data
 * 
 * @returns Object containing context data, loading states, and actions
 */
export const useContextData = (): UseContextDataReturn => {
  const {
    currentContext,
    currentContextData,
    isContextDataLoading,
    contextDataError,
    fetchCurrentContextData,
    fetchContextData,
    clearContextData,
    refreshContextData,
  } = useContextManager();

  /**
   * Get formatted context data for display
   * 
   * @returns Formatted context data suitable for UI display
   */
  const getFormattedContextData = useCallback(() => {
    if (!currentContextData || !currentContext) return null;
    
    return formatContextDataForDisplay(currentContextData, currentContext.contextType);
  }, [currentContextData, currentContext]);

  /**
   * Check if current context data is complete
   * 
   * @returns True if data has all required fields
   */
  const isDataComplete = useCallback(() => {
    if (!currentContextData || !currentContext) return false;
    
    return isContextDataComplete(currentContextData, currentContext.contextType);
  }, [currentContextData, currentContext]);

  /**
   * Fetch context data by type and ID
   * 
   * @param contextType - The context type
   * @param contextId - The context ID
   * @returns Promise containing context data or null
   */
  const fetchContextDataById = useCallback(async (
    contextType: ContextType,
    contextId: string | number
  ): Promise<ContextData | null> => {
    try {
      return await ContextDataService.getContextData(contextType, contextId);
    } catch (error) {
      console.error(`Error fetching ${contextType} data for ID ${contextId}:`, error);
      return null;
    }
  }, []);

  return {
    currentContextData,
    isContextDataLoading,
    contextDataError,
    refreshContextData,
    clearContextData,
    getFormattedContextData,
    isDataComplete,
    fetchContextData,
    fetchContextDataById,
  };
};

/**
 * Hook for fetching context data for a specific context
 * 
 * @param context - The context object to fetch data for
 * @returns Object containing context data, loading state, and actions
 */
export const useContextDataForContext = (context: ContextObject | null) => {
  const [data, setData] = useState<ContextData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!context) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const contextData = await ContextDataService.getContextDataFromObject(context);
      setData(contextData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch context data';
      setError(errorMessage);
      console.error(`Error fetching data for ${context.contextType} ${context.contextId}:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [context]);

  const clearData = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  const refreshData = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Automatically fetch data when context changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getFormattedData = useCallback(() => {
    if (!data || !context) return null;
    
    return formatContextDataForDisplay(data, context.contextType);
  }, [data, context]);

  const isDataComplete = useCallback(() => {
    if (!data || !context) return false;
    
    return isContextDataComplete(data, context.contextType);
  }, [data, context]);

  return {
    data,
    isLoading,
    error,
    fetchData,
    clearData,
    refreshData,
    getFormattedData,
    isDataComplete,
  };
};

/**
 * Hook for batch fetching multiple contexts
 * 
 * @param contexts - Array of context objects to fetch data for
 * @returns Object containing all context data, loading states, and actions
 */
export const useBatchContextData = (contexts: ContextObject[]) => {
  const [allData, setAllData] = useState<Record<string, ContextData | null>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const fetchAllData = useCallback(async () => {
    if (contexts.length === 0) {
      setAllData({});
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const results = await ContextDataService.getMultipleContextData(contexts);
      
      // Create a map of context key to data
      const dataMap: Record<string, ContextData | null> = {};
      const errorMap: Record<string, string | null> = {};

      contexts.forEach((context, index) => {
        const key = `${context.contextType}-${context.contextId}`;
        dataMap[key] = results[index] || null;
        errorMap[key] = results[index] ? null : 'Failed to fetch data';
      });

      setAllData(dataMap);
      setErrors(errorMap);
    } catch (error) {
      console.error('Error fetching batch context data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [contexts]);

  const clearAllData = useCallback(() => {
    setAllData({});
    setErrors({});
  }, []);

  const refreshAllData = useCallback(async () => {
    await fetchAllData();
  }, [fetchAllData]);

  const getDataForContext = useCallback((context: ContextObject) => {
    const key = `${context.contextType}-${context.contextId}`;
    return allData[key] || null;
  }, [allData]);

  const getErrorForContext = useCallback((context: ContextObject) => {
    const key = `${context.contextType}-${context.contextId}`;
    return errors[key] || null;
  }, [errors]);

  const getFormattedDataForContext = useCallback((context: ContextObject) => {
    const data = getDataForContext(context);
    if (!data) return null;
    
    return formatContextDataForDisplay(data, context.contextType);
  }, [getDataForContext]);

  const isDataCompleteForContext = useCallback((context: ContextObject) => {
    const data = getDataForContext(context);
    if (!data) return false;
    
    return isContextDataComplete(data, context.contextType);
  }, [getDataForContext]);

  // Automatically fetch data when contexts change
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  return {
    allData,
    isLoading,
    errors,
    fetchAllData,
    clearAllData,
    refreshAllData,
    getDataForContext,
    getErrorForContext,
    getFormattedDataForContext,
    isDataCompleteForContext,
  };
};

export default useContextData;
